/**
 * UnderwayPhaseExpanded - Enhanced Phase 2 with expandable cards
 */

import React, { useState } from 'react';
import { Navigation, Flag } from 'lucide-react';
import VoyageProgressCard from './cards/VoyageProgressCard';
import NavigationLogCard from './cards/NavigationLogCard';
import WeatherObservationsCard from './cards/WeatherObservationsCard';
import WatchManagementCard from './cards/WatchManagementCard';
import EngineMachineryCard from './cards/EngineMachineryCard';
import SafetySecurityCard from './cards/SafetySecurityCard';
import CommunicationsCard from './cards/CommunicationsCard';
import QuickLogEntriesCard from './cards/QuickLogEntriesCard';

const UnderwayPhaseExpanded = ({ 
  activeVoyage,
  logEntries = [],
  onUpdateVoyage,
  onAllFast,
  onAddLogEntry,
  crew = [],
  currentVessel
}) => {
  // Expanded states for each card
  const [expanded, setExpanded] = useState({
    voyageProgress: true,
    navigationLog: false,
    weather: false,
    watchManagement: false,
    engineMachinery: false,
    safetySecurity: false,
    communications: false,
    quickEntries: false
  });

  // Current voyage state
  const [currentPosition, setCurrentPosition] = useState({
    lat: activeVoyage?.currentPosition?.lat || '',
    lon: activeVoyage?.currentPosition?.lon || '',
    speed: activeVoyage?.currentSpeed || '',
    course: activeVoyage?.currentCourse || '',
    eta: activeVoyage?.eta || ''
  });

  const [navigationEntries, setNavigationEntries] = useState([]);
  const [weatherObservations, setWeatherObservations] = useState([]);
  const [watchChanges, setWatchChanges] = useState([]);
  const [engineData, setEngineData] = useState({
    engineHours: '',
    fuelUsed: '',
    fuelRemaining: '',
    rpm: '',
    temperature: ''
  });

  // Toggle card expansion
  const toggleCard = (cardName) => {
    setExpanded(prev => ({
      ...prev,
      [cardName]: !prev[cardName]
    }));
  };

  // Calculate voyage duration
  const calculateDuration = () => {
    if (!activeVoyage?.actualDepartureTime) return '00:00';
    const start = new Date(activeVoyage.actualDepartureTime);
    const now = new Date();
    const diff = now - start;
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
  };

  // Check if ready for arrival
  const canArrive = () => {
    return currentPosition.lat && currentPosition.lon;
  };

  const handleAllFast = () => {
    if (!canArrive()) {
      alert('Please record current position before arriving');
      return;
    }

    // Update voyage with final data
    if (onUpdateVoyage && activeVoyage) {
      const updatedVoyage = {
        ...activeVoyage,
        finalPosition: currentPosition,
        engineData: engineData,
        weatherObservations: weatherObservations,
        navigationLog: navigationEntries,
        watchChanges: watchChanges
      };
      onUpdateVoyage(updatedVoyage);
    }

    // Call the all fast handler
    if (onAllFast) {
      onAllFast();
    }
  };

  return (
    <div className="space-y-4">
      {/* Phase Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-5 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Navigation className="h-6 w-6 text-blue-600" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">Underway Phase</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {activeVoyage?.departurePort} → {activeVoyage?.destinationPort} • Duration: {calculateDuration()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                Active Voyage
              </span>
            </div>
          </div>
        </div>
        
        <div className="p-5">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500">Duration</p>
              <p className="text-lg font-semibold text-gray-900">{calculateDuration()}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500">Distance</p>
              <p className="text-lg font-semibold text-gray-900">
                {activeVoyage?.distance || '0'} NM
              </p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500">Avg Speed</p>
              <p className="text-lg font-semibold text-gray-900">
                {currentPosition.speed || '0'} kts
              </p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500">Log Entries</p>
              <p className="text-lg font-semibold text-gray-900">{logEntries.length}</p>
            </div>
          </div>

          {/* Expandable Cards */}
          <div className="space-y-4">
            {/* Voyage Progress Card */}
            <VoyageProgressCard
              currentPosition={currentPosition}
              setCurrentPosition={setCurrentPosition}
              activeVoyage={activeVoyage}
              isExpanded={expanded.voyageProgress}
              onToggle={() => toggleCard('voyageProgress')}
              onAddLogEntry={onAddLogEntry}
            />

            {/* Navigation Log Card */}
            <NavigationLogCard
              navigationEntries={navigationEntries}
              setNavigationEntries={setNavigationEntries}
              isExpanded={expanded.navigationLog}
              onToggle={() => toggleCard('navigationLog')}
              onAddLogEntry={onAddLogEntry}
              activeVoyage={activeVoyage}
            />

            {/* Weather Observations Card */}
            <WeatherObservationsCard
              weatherObservations={weatherObservations}
              setWeatherObservations={setWeatherObservations}
              isExpanded={expanded.weather}
              onToggle={() => toggleCard('weather')}
              onAddLogEntry={onAddLogEntry}
              activeVoyage={activeVoyage}
            />

            {/* Watch Management Card */}
            <WatchManagementCard
              watchChanges={watchChanges}
              setWatchChanges={setWatchChanges}
              crew={crew}
              isExpanded={expanded.watchManagement}
              onToggle={() => toggleCard('watchManagement')}
              onAddLogEntry={onAddLogEntry}
              activeVoyage={activeVoyage}
            />

            {/* Engine & Machinery Card */}
            <EngineMachineryCard
              engineData={engineData}
              setEngineData={setEngineData}
              isExpanded={expanded.engineMachinery}
              onToggle={() => toggleCard('engineMachinery')}
              onAddLogEntry={onAddLogEntry}
              activeVoyage={activeVoyage}
            />

            {/* Safety & Security Card */}
            <SafetySecurityCard
              isExpanded={expanded.safetySecurity}
              onToggle={() => toggleCard('safetySecurity')}
              onAddLogEntry={onAddLogEntry}
              activeVoyage={activeVoyage}
              crew={crew}
            />

            {/* Communications Card */}
            <CommunicationsCard
              isExpanded={expanded.communications}
              onToggle={() => toggleCard('communications')}
              onAddLogEntry={onAddLogEntry}
              activeVoyage={activeVoyage}
            />

            {/* Quick Log Entries Card */}
            <QuickLogEntriesCard
              isExpanded={expanded.quickEntries}
              onToggle={() => toggleCard('quickEntries')}
              onAddLogEntry={onAddLogEntry}
              activeVoyage={activeVoyage}
            />
          </div>

          {/* All Fast Button */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleAllFast}
              disabled={!canArrive()}
              className={`px-8 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                canArrive()
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Flag className="h-5 w-5" />
              All Fast - Arrive at Destination
            </button>
          </div>
        </div>
      </div>

      {/* Recent Log Entries */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-medium text-gray-900">Recent Log Entries</h3>
        </div>
        <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
          {logEntries
            .filter(entry => entry.voyageId === activeVoyage?.id)
            .slice(-10)
            .reverse()
            .map((entry, idx) => (
              <div key={entry.id || idx} className="px-5 py-3 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{entry.type}</span>
                    <p className="text-sm text-gray-500">{entry.summary}</p>
                  </div>
                  <span className="text-xs text-gray-400">
                    {new Date(entry.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default UnderwayPhaseExpanded;